import React from 'react';
import Icon from '../../../components/AppIcon';
import { motion } from 'framer-motion';

const SkillsUsedSection = ({ experiences }) => {
  // Extract and count all technologies used across experiences
  const technologyCount = {};
  experiences.forEach(exp => {
    exp.technologies.forEach(tech => {
      technologyCount[tech] = (technologyCount[tech] || 0) + 1;
    });
  });

  // Sort technologies by usage frequency
  const sortedTechnologies = Object.entries(technologyCount)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 20); // Show top 20 technologies

  const getTechIcon = (tech) => {
    const iconMap = {
      'React': 'Code',
      'Node.js': 'Server',
      'JavaScript': 'Code2',
      'TypeScript': 'FileCode',
      'MongoDB': 'Database',
      'GraphQL': 'Share',
      'Redux': 'Layers',
      'Express': 'Server',
      'Next.js': 'Globe',
      'Git': 'GitBranch',
      'Wordpress': 'Code'
    };
    return iconMap[tech] || 'Code';
  };

  const getIntensityColor = (count, maxCount) => {
    const intensity = count / maxCount;
    if (intensity >= 0.8) return 'bg-primary text-primary-foreground border-primary';
    if (intensity >= 0.6) return 'bg-primary/80 text-primary-foreground border-primary/80';
    if (intensity >= 0.4) return 'bg-primary/60 text-primary-foreground border-primary/60';
    if (intensity >= 0.2) return 'bg-primary/40 text-primary border-primary/40';
    return 'bg-primary/20 text-primary border-primary/20';
  };

  const getDisplayCount = (tech, count) => {
    const tenPlusTechs = [
      'React', 'Node.js', 'JavaScript', 'MongoDB', 'Redux', 'Express', 'Express.js', 'Next.js', 'Git', 'REST APIs', 'REST API', 'Mongoose'
    ];
    if (tenPlusTechs.includes(tech)) return '10+';
    if (tech === 'AWS') return '3+';
    if (tech === 'System Architecture') return '2+';
    if (tech === 'WordPress' || tech === 'Wordpress') return '8+';
    return count;
  };

  const maxCount = Math.max(...Object.values(technologyCount));

  return (
    <div className="bg-surface rounded-lg border border-border p-8 mb-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center border border-accent/20">
          <Icon name="Code" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-text-primary">Technologies & Skills Used</h3>
          <p className="text-sm text-text-secondary">Skills gained throughout my professional experience</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {sortedTechnologies.map(([tech, count], index) => {
          const displayCount = getDisplayCount(tech, count);
          return (
            <motion.div
              key={tech}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className={`relative group cursor-pointer rounded-lg border p-4 text-center transition-all duration-300 hover:scale-105 hover:shadow-md ${getIntensityColor(count, maxCount)}`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Icon name={getTechIcon(tech)} size={24} />
                <span className="text-sm font-medium">{tech}</span>
                <span className="text-xs opacity-80">{displayCount} project{displayCount === 1 ? '' : 's'}</span>
              </div>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-text-primary text-background text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                Used in {displayCount} professional {displayCount === 1 ? 'experience' : 'experiences'}
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} />
            <span>Skills intensity based on professional usage frequency</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-sm"></div>
              <span>Most Used</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary/20 rounded-sm"></div>
              <span>Least Used</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsUsedSection;